import { isImgixUrl, resolveByosImgixSrc } from '@air/component-imgix-image';
import { type AppNode, type ImageData, type TextData, type VideoData } from '@air/feature-edit-canvas';

import { type CanvasDetail } from '~/swr-hooks/canvases/types';

const getVideoThumbnailUrl = (videoUrl: string): string => {
  const resolvedUrl = resolveByosImgixSrc(videoUrl);

  if (!isImgixUrl(resolvedUrl)) {
    return resolvedUrl;
  }

  try {
    const url = new URL(resolvedUrl);
    url.searchParams.set('video-thumbnail', 'auto');
    url.searchParams.set('fm', 'jpg');
    return url.toString();
  } catch {
    return resolvedUrl;
  }
};

/**
 * Converts API canvas nodes to the format expected by EditCanvas store.
 * Uses the SDK's discriminated union on data.type to distinguish node kinds.
 */
export const convertCanvasNodesToAppNodes = (nodes: CanvasDetail['nodes']): AppNode[] => {
  return nodes.flatMap((node): AppNode[] => {
    // Group nodes are metadata — not rendered directly.
    // Frontend keys off individual nodes' groupId property instead.
    if (node.data.type === 'group') {
      return [];
    }

    const base = {
      groupId: node.groupId ?? undefined,
      id: node.id,
      position: node.position,
      remoteId: node.id,
      style: { height: node.height, width: node.width },
      updatedAt: node.updatedAt,
      zIndex: Number(node.zIndex),
    };

    if (node.data.type === 'text') {
      const data: TextData = {
        fontColor: node.data.fontColor,
        fontFamily: node.data.fontFamily,
        fontSize: node.data.fontSize,
        fontVariant: node.data.fontVariant,
        letterSpacing: node.data.letterSpacing,
        lineHeight: node.data.lineHeight,
        source: node.data.source,
        text: node.data.text,
        textAlign: node.data.textAlign,
        fontSource: node.data.fontSource,
        ...(node.data.fontSource === 'file' && node.data.fontFileUrl ? { fontFileUrl: node.data.fontFileUrl } : {}),
        ...(node.data.fontSource !== 'file' && node.data.fontMetadataUrl
          ? { fontMetadataUrl: node.data.fontMetadataUrl }
          : {}),
        type: 'text',
      };

      return [{ ...base, data, type: 'text' }];
    }

    if (node.data.type === 'video') {
      const data: VideoData = {
        fileName: node.data.fileName ?? '',
        media: { original: node.data.media.original ?? '' },
        prompt: node.data.prompt,
        smartResizeFormatId: node.data.smartResizeFormatId,
        source: node.data.source,
        thumbnailUrl: node.data.media.original ? getVideoThumbnailUrl(node.data.media.original) : undefined,
        type: 'video',
      };

      return [{ ...base, data, type: 'video' }];
    }

    const nodeData = node.data;
    const imageData: ImageData = {
      assetId: nodeData.assetId,
      fileName: nodeData.fileName,
      media: nodeData.media,
      prompt: nodeData.prompt,
      smartResizeFormatId: nodeData.smartResizeFormatId,
      source: nodeData.source,
      type: 'image',
      versionId: nodeData.versionId,
    };

    return [{ ...base, data: imageData, type: 'image' }];
  });
};
